"use client";

import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { MarketOutcome } from "@/lib/api";
import { pickLocale, formatPriceCents, formatVolumeCents } from "@/lib/format";
import { getOutcomeIcon } from "@/lib/outcome-icons";
import { useDisplayCurrency } from "@/lib/useDisplayCurrency";
import { useFxRate } from "@/lib/useFxRate";

/**
 * EPIC-053 STORY-A — Polymarket-parity outcome row list.
 *
 * Canonical multi-outcome listing component for the market detail
 * page. Replaces the flat outcome card grid that lived in
 * `apps/web/app/markets/[slug]/page.tsx`. Renders one row per
 * outcome:
 *
 *   - Left: leading icon (flag emoji for countries, glyph for
 *     yes/no/draw/over/under, colored 2-letter bubble fallback).
 *   - Middle: outcome name + 24h volume in small text.
 *   - Right-middle: current price as a probability % + (optional)
 *     24h delta arrow + cents change. Reads `priceDelta24hCents`
 *     defensively so the component renders before Agent D's schema
 *     wire-up lands; absent → renders "—" in the delta slot.
 *   - Right: inline Buy Yes <price>¢ (green) + Buy No <price>¢
 *     (red) buttons. Clicking either calls `onSelect(id)` AND
 *     `onBuy(id, side)` so the trade panel picks up the chosen
 *     outcome + side without a round-trip through a modal.
 *
 * Selected-row visual: brand-color outline so the user can see
 * which row drives the right-rail trade panel at a glance. Click
 * on the row itself (outside the Buy buttons) calls `onSelect(id)`
 * only.
 *
 * Mobile parity (CLAUDE.md hard rule, 375 / 768 / ≥1024 breakpoints):
 * rows collapse to a single-column card stack at <640px width with
 * the Buy buttons stretching to full width — touch targets stay at
 * 44px min-height per the iOS / Material guideline.
 *
 * Sort order: by current price descending so the strongest favorite
 * leads the list (Polymarket pattern). Ties broken by `sortOrder`
 * ascending so the order is stable across renders.
 */
export type OutcomeBuySide = "yes" | "no";

export interface OutcomeRowListProps {
  outcomes: MarketOutcome[];
  selectedOutcomeId: string | null;
  onSelect: (outcomeId: string) => void;
  onBuy: (outcomeId: string, side: OutcomeBuySide) => void;
  viewerLocale: string;
}

// Defensive shape for the priceDelta24hCents field Agent D is adding
// to MarketOutcome on the API side. Reading via `unknown` cast keeps
// this row list compatible with the type before the API rolls; once
// the field lands on the canonical type the cast becomes a no-op.
function readPriceDelta(o: MarketOutcome): number | null {
  const candidate = (o as unknown as { priceDelta24hCents?: number | null })
    .priceDelta24hCents;
  if (typeof candidate !== "number" || !Number.isFinite(candidate)) return null;
  return candidate;
}

export function OutcomeRowList({
  outcomes,
  selectedOutcomeId,
  onSelect,
  onBuy,
  viewerLocale,
}: OutcomeRowListProps) {
  const { t } = useTranslation();
  // 2026-05-19 — locale-aware volume display. BR viewers see R$ with
  // fx-projected cents; off-region viewers see the USDC-anchored $.
  const displayCurrency = useDisplayCurrency();
  const fx = useFxRate("BRL");
  const usdcPerBrl = fx?.rate ?? null;

  const sorted = useMemo(
    () =>
      [...outcomes].sort((a, b) => {
        if (b.currentPriceCents !== a.currentPriceCents) {
          return b.currentPriceCents - a.currentPriceCents;
        }
        return a.sortOrder - b.sortOrder;
      }),
    [outcomes],
  );

  return (
    <ul className="flex flex-col gap-2">
      {sorted.map((outcome) => {
        const label = pickLocale(outcome.name, viewerLocale);
        // Migration 0166 — admin-controlled per-outcome icon override.
        // When outcome.iconUrl is set:
        //   - 'emoji:<glyph>' prefix → emoji rendered as unicode text
        //   - 'https://...' URL → rendered as <img>
        // When null, fall back to the existing auto-detect chain.
        const iconUrl = outcome.iconUrl ?? null;
        const iconOverrideEmoji =
          iconUrl !== null && iconUrl.startsWith("emoji:")
            ? iconUrl.slice("emoji:".length)
            : null;
        const iconOverrideImage =
          iconUrl !== null && !iconUrl.startsWith("emoji:")
            ? iconUrl
            : null;
        const icon = getOutcomeIcon({ name: label });
        const yesCents = outcome.currentPriceCents;
        const noCents = 100 - yesCents;
        const delta = readPriceDelta(outcome);
        const isSelected = selectedOutcomeId === outcome.id;
        return (
          <li key={outcome.id}>
            {/* Row body is a button so the entire surface is one
                tap target on mobile + announces as actionable to
                screen readers. The inner Buy buttons stop the
                bubble so they don't double-fire selection from the
                outer click handler. */}
            <div
              role="group"
              aria-label={t("outcomeRowList.rowGroupAria", { name: label })}
              className={`flex min-h-[44px] flex-col gap-2 rounded-lg border bg-[var(--color-surface)] p-3 transition-colors sm:flex-row sm:items-center ${
                isSelected
                  ? "border-[#b8924f] bg-[rgba(184,146,79,0.10)]"
                  : "border-[var(--color-border)] hover:border-[#b8924f]"
              }`}
            >
              <button
                type="button"
                onClick={() => onSelect(outcome.id)}
                aria-pressed={isSelected}
                aria-label={t("outcomeRowList.selectRowAria", { name: label })}
                className="flex min-w-0 flex-1 items-center gap-3 rounded-md text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]"
              >
                <span
                  aria-hidden="true"
                  className="flex h-9 w-9 flex-shrink-0 items-center justify-center text-[22px] leading-none"
                >
                  {iconOverrideImage ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={iconOverrideImage}
                      alt=""
                      width={36}
                      height={36}
                      className="h-9 w-9 rounded-full object-cover"
                    />
                  ) : iconOverrideEmoji ? (
                    iconOverrideEmoji
                  ) : icon.kind === "emoji" ? (
                    icon.emoji
                  ) : (
                    <span
                      className="flex h-9 w-9 items-center justify-center rounded-full text-[11px] font-bold text-white"
                      style={{ background: icon.color }}
                    >
                      {icon.initials}
                    </span>
                  )}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-semibold text-[var(--color-text)]">
                    {label}
                  </span>
                  <span className="block text-[10px] text-[var(--color-text3)]">
                    {t("outcomeRowList.volumeTemplate", {
                      vol: formatVolumeCents(outcome.volumeCents, displayCurrency, usdcPerBrl),
                    })}
                  </span>
                </span>
                <span className="flex-shrink-0 text-right">
                  <span
                    className="block font-mono text-[16px] font-bold tabular-nums text-[var(--color-text)]"
                    title={t("outcomeRowList.currentProbability")}
                  >
                    {yesCents}%
                  </span>
                  {delta === null ? (
                    <span className="block font-mono text-[10px] tabular-nums text-[var(--color-text3)]">
                      —
                    </span>
                  ) : delta === 0 ? (
                    <span className="block font-mono text-[10px] tabular-nums text-[var(--color-text3)]">
                      0¢
                    </span>
                  ) : (
                    <span
                      className="block font-mono text-[10px] tabular-nums"
                      style={{
                        color:
                          delta > 0
                            ? "var(--color-yes)"
                            : "var(--color-no)",
                      }}
                    >
                      {delta > 0 ? "▲" : "▼"} {Math.abs(delta)}¢
                    </span>
                  )}
                </span>
              </button>

              {/* Inline Buy Yes / Buy No — Polymarket-style chips.
                  Each button calls onSelect FIRST so the row
                  highlight + trade panel both reflect the chosen
                  outcome immediately, then onBuy with the side so
                  the panel can pre-bind buy/sell mode + scroll into
                  view on mobile. Min-height 44px keeps the touch
                  target ergonomic on phones. */}
              <div className="flex flex-shrink-0 gap-2 sm:w-[220px]">
                <button
                  type="button"
                  onClick={() => {
                    onSelect(outcome.id);
                    onBuy(outcome.id, "yes");
                  }}
                  className="min-h-[44px] flex-1 rounded-md px-2 py-2 text-[11px] font-bold transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]"
                  style={{
                    background:
                      "color-mix(in srgb, var(--color-yes) 18%, transparent)",
                    color: "var(--color-yes)",
                  }}
                  aria-label={t("outcomeRowList.buyYesAria", {
                    name: label,
                    cents: yesCents,
                  })}
                >
                  {t("outcomeRowList.buyYesTemplate", { cents: yesCents })}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    onSelect(outcome.id);
                    onBuy(outcome.id, "no");
                  }}
                  className="min-h-[44px] flex-1 rounded-md px-2 py-2 text-[11px] font-bold transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]"
                  style={{
                    background:
                      "color-mix(in srgb, var(--color-no) 18%, transparent)",
                    color: "var(--color-no)",
                  }}
                  aria-label={t("outcomeRowList.buyNoAria", {
                    name: label,
                    cents: noCents,
                  })}
                >
                  {t("outcomeRowList.buyNoTemplate", { cents: noCents })}
                </button>
              </div>
            </div>

            {/* sr-only summary so a screen-reader user who skims the
                list without expanding the trade panel still hears
                "Finland — 21¢, volume $12k". */}
            <span className="sr-only">
              {t("outcomeRowList.summaryTemplate", {
                name: label,
                price: formatPriceCents(yesCents),
                volume: formatVolumeCents(outcome.volumeCents, displayCurrency, usdcPerBrl),
              })}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
